// Cashflow-Engine: Wochen-Expansion der Budget-Einträge (aus CashflowPlanung
// extrahiert, ADR 0043). Pure Funktionen ohne React — genutzt von der
// Cashflow-Ansicht UND der Szenarien-Simulation, damit beide exakt dieselbe
// Verteil-Logik verwenden (kein Drift zwischen Ansicht und Simulation).
import { calculateBudgetEntryStatus } from "./azureBudgetService";

// ===== Wochen-Helfer =====

export const getWeekStart = (date, weeksOffset = 0) => {
  const result = new Date(date);
  const dayOfWeek = result.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  result.setDate(result.getDate() + diff + weeksOffset * 7);
  result.setHours(0, 0, 0, 0);
  return result;
};

export const getWeekEnd = (weekStart) => {
  const result = new Date(weekStart);
  result.setDate(result.getDate() + 6);
  result.setHours(23, 59, 59, 999);
  return result;
};

export const getWeekNumber = (date) => {
  // ISO 8601 week number
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

export const formatWeekLabel = (weekStart) => {
  const weekEnd = getWeekEnd(weekStart);
  const weekNum = getWeekNumber(weekStart);
  return `KW${weekNum} (${weekStart.getDate()}.${
    weekStart.getMonth() + 1
  } - ${weekEnd.getDate()}.${weekEnd.getMonth() + 1})`;
};

export const formatShortWeekLabel = (weekStart) => {
  const weekNum = getWeekNumber(weekStart);
  return `KW${weekNum}`;
};

// ===== Basis-Berechnungen =====

// ===== Eigenanteil: Rückstellung für Projekt-Ausgaben (Zahlungsplan-basiert) =====
//
// Modell (Vorgabe GL 2026-09-04): Subplaner:innen werden für die effektiv
// geleistete Arbeit bezahlt, und unser Abrechnungsfortschritt ist das Mass dafür.
// Von einem Zahlungseingang gehört uns deshalb nur der Teil, der nach Rückstellung
// der anteilig bereits verdienten Honorare übrig bleibt:
//
//   Fortschritt(t) = unsere Einnahmen bis t / unsere Gesamteinnahmen der Gruppe
//   verdient_P(t)  = Gesamthonorar von P x Fortschritt(t)
//   Schuld_P(t)    = max(0, verdient_P(t) − bereits an P bezahlt bis t)
//
// Beispiel: wir werden 6x bezahlt, Subplanerin X 2x. Nach unserer ersten Zahlung
// hat X ein Sechstel ihres Gesamthonorars verdient — das ist ein Drittel ihrer
// ersten Zahlung. Genau diese Rückstellung wächst mit jedem Eingang und fällt
// weg, sobald X tatsächlich bezahlt wird.
//
// Die Schuld ist eine BESTANDSgrösse: kumulativEigenanteil = kumulativ − Schuld(t).
// Deshalb liegt der Eigenanteil immer unter dem Cashflow (Schuld nie negativ), und
// die Zahlung an eine Subplanerin bewegt die Linie nicht (Abfluss und wegfallende
// Rückstellung heben sich exakt auf).
//
// Gruppierung: pro Phase, sofern die Phase eigene Einnahmen hat, sonst pro Projekt
// (Phasen sind teils als ID, teils als Name gespeichert — `phaseNames` normalisiert).
// Erfasst werden ALLE einmaligen Projekt-Ausgaben, auch ohne Partner:in (Drittkosten,
// Gebühren) — sie mindern unseren Anteil genauso. Nicht erfasst (und damit wie
// Overhead 1:1 auf der Linie): Prognose-Einträge, regelmässige Einträge und
// Ausgaben ohne Projekt.

const GRUPPE_PROJEKT = "__projekt__";
const VERGANGEN = new Date(0);

const eintragBetrag = (entry) => entry.brutto_betrag || entry.betrag || 0;

// Datum, an dem der Eintrag in der Cashflow-Linie kassenwirksam wird — muss exakt
// der Platzierung in distributeEinmaligeEintrag folgen, sonst heben sich Abfluss
// und wegfallende Rückstellung nicht auf.
const getKassenDatum = (entry, today, naechsteWoche) => {
  // Bereits bezahlt / im Journal erfasst: steckt schon im Bank-Startsaldo
  if (entry.status === "paid" || entry.paid_date || entry.ist_erfasst) return VERGANGEN;

  const dateStr = entry.versand_datum || entry.wann;
  if (!dateStr) return null;

  const datum = new Date(dateStr);
  if (isNaN(datum.getTime())) return null;
  const zahlungsfrist = parseInt(entry.zahlungsfrist) || 0;
  if (zahlungsfrist > 0) datum.setDate(datum.getDate() + zahlungsfrist);

  const status = calculateBudgetEntryStatus(entry);
  const isPastUnpaidExpense =
    entry.typ === "Ausgabe" &&
    status !== "paid" &&
    entry.automatic_payment !== true &&
    !entry.ist_mwst_abrechnung &&
    datum < today;

  // Überfällige Einnahmen und vergangene unbezahlte Ausgaben rücken in der Engine
  // in die nächste Woche ab heute nach (ADR 0041) — hier gleich behandeln.
  if ((status === "overdue" && entry.typ === "Einnahme") || isPastUnpaidExpense) {
    return naechsteWoche;
  }

  return datum;
};

export const buildEigenanteilKontext = (budgetEntries = [], phaseNames = {}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const naechsteWoche = getWeekStart(today, 1);

  const phaseKeyOf = (entry) => phaseNames[entry.phase] || entry.phase || "";
  const gruppenId = (projekt, phaseKey) => `${projekt}||${phaseKey}`;

  // Relevante Einträge: einmalig, keine Prognose, keine MwSt-Abrechnung, mit Projekt
  const relevant = budgetEntries.filter(
    (entry) =>
      entry.projekt &&
      entry.projekt.trim() !== "" &&
      !entry.ist_prognose &&
      !entry.ist_mwst_abrechnung &&
      entry.typ_timing !== "Regelmässig"
  );

  // 1. Durchgang: Einnahmen je Phase — entscheidet, ob eine Phase eine eigene
  // Bezugsgruppe bildet oder auf die Projektebene zurückfällt
  const einnahmenProPhase = {};
  relevant.forEach((entry) => {
    if (entry.typ !== "Einnahme") return;
    const phaseKey = phaseKeyOf(entry);
    if (!phaseKey) return;
    const id = gruppenId(entry.projekt, phaseKey);
    einnahmenProPhase[id] = (einnahmenProPhase[id] || 0) + eintragBetrag(entry);
  });

  const gruppeFuer = (entry) => {
    const phaseKey = phaseKeyOf(entry);
    if (phaseKey && einnahmenProPhase[gruppenId(entry.projekt, phaseKey)] > 0) {
      return gruppenId(entry.projekt, phaseKey);
    }
    return gruppenId(entry.projekt, GRUPPE_PROJEKT);
  };

  // 2. Durchgang: je Gruppe die Einnahmen-Ereignisse, je Gruppe+Partner:in die
  // Honorar-Zahlungen (jeweils mit Kassendatum)
  const gruppen = {};
  const gruppeAnlegen = (id, projekt) => {
    if (!gruppen[id]) {
      gruppen[id] = {
        projekt,
        einnahmenTotal: 0,
        einnahmen: [], // {datum, betrag}
        partner: {}, // partnerKey -> {honorarTotal, zahlungen: [{datum, betrag}]}
      };
    }
    return gruppen[id];
  };

  relevant.forEach((entry) => {
    const datum = getKassenDatum(entry, today, naechsteWoche);
    if (!datum) return;
    const betrag = eintragBetrag(entry);
    const id = gruppeFuer(entry);
    const gruppe = gruppeAnlegen(id, entry.projekt);

    if (entry.typ === "Einnahme") {
      gruppe.einnahmen.push({ datum, betrag });
    } else if (entry.typ === "Ausgabe") {
      const partnerKey = entry.partner_id || entry.partner_name || "other";
      if (!gruppe.partner[partnerKey]) {
        gruppe.partner[partnerKey] = { honorarTotal: 0, zahlungen: [] };
      }
      gruppe.partner[partnerKey].honorarTotal += betrag;
      gruppe.partner[partnerKey].zahlungen.push({ datum, betrag });
    }
  });

  // Auf Projektebene zählt für den Fortschritt JEDE Projekt-Einnahme, unabhängig
  // davon, in welcher Phasen-Gruppe sie gelandet ist — sonst hätten Ausgaben ohne
  // eigene Einnahmen-Phase keinen Fortschrittsmassstab. Zähler und Nenner stammen
  // dabei aus derselben Menge (nur Einträge mit gültigem Kassendatum).
  const projektEinnahmen = {};
  relevant.forEach((entry) => {
    if (entry.typ !== "Einnahme") return;
    const datum = getKassenDatum(entry, today, naechsteWoche);
    if (!datum) return;
    if (!projektEinnahmen[entry.projekt]) projektEinnahmen[entry.projekt] = [];
    projektEinnahmen[entry.projekt].push({ datum, betrag: eintragBetrag(entry) });
  });

  const istProjektGruppe = (id) => id.endsWith(`||${GRUPPE_PROJEKT}`);

  Object.entries(gruppen).forEach(([id, gruppe]) => {
    const quelle = istProjektGruppe(id)
      ? projektEinnahmen[gruppe.projekt] || []
      : gruppe.einnahmen;
    gruppe.einnahmenTotal = quelle.reduce((s, e) => s + e.betrag, 0);
  });

  const fortschritt = (id, gruppe, bis) => {
    if (!gruppe.einnahmenTotal) return 0;
    const quelle = istProjektGruppe(id)
      ? projektEinnahmen[gruppe.projekt] || []
      : gruppe.einnahmen;
    const bisher = quelle.reduce((s, e) => (e.datum <= bis ? s + e.betrag : s), 0);
    return Math.min(1, bisher / gruppe.einnahmenTotal);
  };

  // Offene Rückstellung über ALLE Projekte zum Zeitpunkt `bis`
  const schuldAm = (bis) =>
    Object.entries(gruppen).reduce((total, [id, gruppe]) => {
      const anteil = fortschritt(id, gruppe, bis);
      if (!anteil) return total;
      return (
        total +
        Object.values(gruppe.partner).reduce((s, p) => {
          const bezahlt = p.zahlungen.reduce(
            (sum, z) => (z.datum <= bis ? sum + z.betrag : sum),
            0
          );
          return s + Math.max(0, p.honorarTotal * anteil - bezahlt);
        }, 0)
      );
    }, 0);

  // Anzeige-Quote pro Einnahme-Eintrag (Badge in der Detailtabelle): welcher Anteil
  // dieser Zahlung bleibt uns nach Rückstellung der Honorare der Gruppe
  const quoteFuer = (entry) => {
    if (!entry.projekt || entry.projekt.trim() === "") return 1;
    const gruppe = gruppen[gruppeFuer(entry)];
    if (!gruppe || !gruppe.einnahmenTotal) return 1;
    const honorare = Object.values(gruppe.partner).reduce(
      (s, p) => s + p.honorarTotal,
      0
    );
    return Math.max(0, Math.min(1, 1 - honorare / gruppe.einnahmenTotal));
  };

  return { schuldAm, quoteFuer };
};

export const calculateCurrentBankSaldo = (bookings) => {
  let saldo = 0;

  bookings.forEach((booking) => {
    if (booking.Konto === 1020) {
      saldo += booking.Soll || 0;
      saldo -= booking.Haben || 0;
    } else if (booking.Gegenkonto === 1020) {
      saldo -= booking.Soll || 0;
      saldo += booking.Haben || 0;
    }
  });

  return saldo;
};

// Anzeige-Name fuer Projekt-Ausgaben (Subplaner:in): {Projektcode} - {Phase} - {Firma}
// Nur wenn Projekt UND Firma erfasst sind; unaufloesbare Phasen-IDs werden weggelassen
export const getProjektAusgabeAnzeigeName = (entry, phaseNames = {}) => {
  if (entry.typ !== "Ausgabe" || !entry.projekt || !entry.partner_name) return null;
  const phaseName = phaseNames[entry.phase] || entry.phase || "";
  const parts = [entry.projekt];
  if (phaseName && !/^\d{10,}_/.test(phaseName)) parts.push(phaseName);
  parts.push(entry.partner_name);
  return parts.join(" - ");
};

// ===== Verteil-Logik (intern) =====

const distributeEinmaligeEintrag = (entry, weeks, istPrognose = false, quoteFuer = () => 1, phaseNames = {}) => {
  const dateStr = entry.versand_datum || entry.wann;
  if (!dateStr || entry.ist_erfasst) return;

  // Cashflow: position at Fälligkeitsdatum (when money actually flows)
  const zahlungsfrist = parseInt(entry.zahlungsfrist) || 0;
  let effectiveDate = dateStr;
  if (zahlungsfrist > 0) {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + zahlungsfrist);
    effectiveDate = d.toISOString().split("T")[0];
  }

  const entryDate = new Date(effectiveDate);

  // NEU: Berechne Status für alle Einträge
  const entryStatus = calculateBudgetEntryStatus(entry);

  let targetWeek;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Toter-Winkel-Fix: Unbezahlte Ausgaben mit Fälligkeit in der Vergangenheit
  // dürfen nicht stillschweigend aus dem Vorwärtsfenster fallen — nur was auf
  // "paid" gesetzt wird verschwindet (automatische Zahlungen und synthetische
  // MwSt-Einträge ausgenommen). Sie rücken wie überfällige Einnahmen in die
  // nächste Woche ab heute.
  const isPastUnpaidExpense =
    entry.typ === "Ausgabe" &&
    entryStatus !== "paid" &&
    entry.automatic_payment !== true &&
    !entry.ist_mwst_abrechnung &&
    entryDate < today;

  const isOverdue = entryStatus === "overdue" || isPastUnpaidExpense;

  // Überfällige Einnahmen und vergangene unbezahlte Ausgaben: verschiebe auf
  // die nächste Woche AB HEUTE
  // ABER: Nur in der Ansicht, die das AKTUELLE Datum enthält (nicht in
  // zukünftigen/vergangenen Zeitfenstern — sonst würden sie doppelt gezählt)
  if ((entryStatus === "overdue" && entry.typ === "Einnahme") || isPastUnpaidExpense) {
    // Prüfe ob der betrachtete Zeitraum das aktuelle Datum enthält
    const containsToday = weeks.some(week => week.weekStart <= today && week.weekEnd >= today);

    if (containsToday) {
      // Finde die erste Woche, die NACH heute beginnt
      targetWeek = weeks.find(week => week.weekStart > today);
    }
    // Sonst: targetWeek bleibt undefined → wird nicht angezeigt
  } else {
    targetWeek = weeks.find(
      (week) => entryDate >= week.weekStart && entryDate <= week.weekEnd
    );
  }

  if (targetWeek) {
    const betrag = entry.brutto_betrag || entry.betrag;
    const projekt = entry.projekt || "";
    const ratio = entry.typ === "Einnahme" ? quoteFuer(entry) : 1;
    const eigenanteilBetrag = entry.typ === "Einnahme" ? betrag * ratio : 0;

    if (istPrognose) {
      if (entry.typ === "Einnahme") {
        targetWeek.einnahmenPrognose += betrag;
      } else {
        targetWeek.ausgabenPrognose += betrag;
      }
    } else {
      if (entry.typ === "Einnahme") {
        if (isOverdue) {
          targetWeek.einnahmenOverdue += betrag;
        } else {
          targetWeek.einnahmen += betrag;
          // Nur mit versendeter Rechnung "voll grün" (reiner Anzeige-Split)
          if (entryStatus === "sent") {
            targetWeek.einnahmenVersendet += betrag;
          } else {
            targetWeek.einnahmenUnversendet += betrag;
          }
        }
      } else {
        targetWeek.ausgaben += betrag;
        if (projekt) {
          targetWeek.ausgabenProjekt += betrag;
        }
      }
    }

    const detail = {
      id: entry.id,
      name: entry.name,
      displayName: getProjektAusgabeAnzeigeName(entry, phaseNames),
      typ: entry.typ,
      betrag: betrag,
      netto_betrag: entry.netto_betrag || entry.betrag,
      mwst_betrag: entry.mwst_betrag || 0,
      mwst_satz: entry.mwst_satz || 0,
      timing: "Einmalig",
      datum: entryDate.toLocaleDateString("de-CH"),
      ist_mwst_abrechnung: entry.ist_mwst_abrechnung || false,
      ist_prognose: istPrognose || false,
      status: entryStatus || entry.status || "expected",
      computedStatus: entryStatus || entry.status || "expected",
      is_overdue: isOverdue,
      original_date: isOverdue ? entryDate.toLocaleDateString("de-CH") : null,
      automatic_payment: entry.automatic_payment || false,
      projekt: projekt,
      eigenanteil: eigenanteilBetrag,
      eigenanteilRatio: ratio,
    };

    if (entry.ist_mwst_abrechnung) {
      targetWeek.mwstDetails.push(detail);
    } else {
      targetWeek.budgetDetails.push(detail);
    }
  }
};

const distributeRegelmässigeEintrag = (entry, weeks, istPrognose = false, allEntries = [], quoteFuer = () => 1, phaseNames = {}) => {
  if (!entry.ab_wann || entry.ist_erfasst) return;

  const startDate = new Date(entry.ab_wann);
  const endDate = entry.bis_wann ? new Date(entry.bis_wann) : null;
  const projekt = entry.projekt || "";
  // Regelmässige Einträge sind nicht Teil des Rückstellungs-Modells (Gesamthonorar
  // ist ohne Expansion unbekannt) — Badge deshalb neutral
  const ratio = 1;

  weeks.forEach((week) => {
    if (week.weekEnd < startDate) return;
    if (endDate && week.weekStart > endDate) return;

    const shouldInclude = shouldIncludeInWeek(entry, week, startDate);

    if (shouldInclude) {
      const instanceDate = calculateInstanceDateForWeek(entry, week, startDate);
      const instancePeriod = instanceDate.toLocaleDateString("de-CH", {
        month: "long",
        year: "numeric",
      });

      const hasOneTimeCopy = allEntries.some(
        (e) =>
          e.created_from_recurring_id === entry.id &&
          e.instance_period === instancePeriod
      );

      if (hasOneTimeCopy) {
        return;
      }

      const betrag = entry.brutto_betrag || entry.betrag;
      const eigenanteilBetrag = entry.typ === "Einnahme" ? betrag * ratio : 0;

      if (istPrognose) {
        if (entry.typ === "Einnahme") {
          week.einnahmenPrognose += betrag;
        } else {
          week.ausgabenPrognose += betrag;
        }
      } else {
        if (entry.typ === "Einnahme") {
          week.einnahmen += betrag;
          // Wiederkehrende Instanzen: Rechnung per Definition noch nicht versendet
          week.einnahmenUnversendet += betrag;
        } else {
          week.ausgaben += betrag;
          if (projekt) {
            week.ausgabenProjekt += betrag;
          }
        }
      }

      const entryStatus = calculateBudgetEntryStatus(entry);
      const defaultStatus = entry.typ === "Ausgabe" ? "expecting" : "expected";

      week.budgetDetails.push({
        id: entry.id,
        name: entry.name,
        displayName: getProjektAusgabeAnzeigeName(entry, phaseNames),
        typ: entry.typ,
        betrag: betrag,
        netto_betrag: entry.netto_betrag || entry.betrag,
        mwst_betrag: entry.mwst_betrag || 0,
        mwst_satz: entry.mwst_satz || 0,
        timing: `${entry.haeufigkeit} (${entry.ab_wann}${
          entry.bis_wann ? ` - ${entry.bis_wann}` : ""
        })`,
        datum: "Regelmässig",
        ist_mwst_abrechnung: false,
        ist_prognose: istPrognose || false,
        computedStatus: defaultStatus,
        ist_wiederkehrend: true,
        instanceDate: instanceDate,
        recurring_entry: entry,
        automatic_payment: entry.automatic_payment || false,
        projekt: projekt,
        eigenanteil: eigenanteilBetrag,
        eigenanteilRatio: ratio,
      });
    }
  });
};

const calculateInstanceDateForWeek = (entry, week, startDate) => {
  // Berechne das spezifische Datum der wiederkehrenden Zahlung in dieser Woche
  const startDay = startDate.getDate();
  const weekYear = week.weekStart.getFullYear();
  const weekMonth = week.weekStart.getMonth();
  const daysInMonth = new Date(weekYear, weekMonth + 1, 0).getDate();
  const targetDay = Math.min(startDay, daysInMonth);
  const targetDate = new Date(weekYear, weekMonth, targetDay);

  // Stelle sicher, dass das Datum in der Woche liegt
  if (targetDate >= week.weekStart && targetDate <= week.weekEnd) {
    return targetDate;
  }

  // Fallback: Verwende den Start der Woche
  return week.weekStart;
};

const shouldIncludeInWeek = (entry, week, startDate) => {
  switch (entry.haeufigkeit) {
    case "monatlich":
      return isMonthlyPaymentWeek(week, startDate);
    case "quartalsweise": {
      // Month-based quarterly check: every 3 months from start date
      const startMonth = startDate.getFullYear() * 12 + startDate.getMonth();
      const weekMonth = week.weekStart.getFullYear() * 12 + week.weekStart.getMonth();
      const monthDiff = weekMonth - startMonth;
      if (monthDiff < 0 || monthDiff % 3 !== 0) return false;
      // Check if the payment day falls within this week
      const qDay = Math.min(startDate.getDate(), new Date(week.weekStart.getFullYear(), week.weekStart.getMonth() + 1, 0).getDate());
      const qDate = new Date(week.weekStart.getFullYear(), week.weekStart.getMonth(), qDay);
      return qDate >= week.weekStart && qDate <= week.weekEnd;
    }
    case "jährlich": {
      const startWeekNumber = getWeekNumber(startDate);
      return week.weekNumber === startWeekNumber;
    }
    default:
      return false;
  }
};

const isMonthlyPaymentWeek = (week, startDate) => {
  if (startDate >= week.weekStart && startDate <= week.weekEnd) {
    return true;
  }

  const startDay = startDate.getDate();
  const weekYear = week.weekStart.getFullYear();
  const weekMonth = week.weekStart.getMonth();
  const daysInMonth = new Date(weekYear, weekMonth + 1, 0).getDate();
  const targetDay = Math.min(startDay, daysInMonth);
  const targetDate = new Date(weekYear, weekMonth, targetDay);

  return targetDate >= week.weekStart && targetDate <= week.weekEnd;
};

// ===== Wochen-Expansion =====

export const generateExtendedCashflow = (
  budgetEntries,
  startSaldo,
  startDate,
  weekCount,
  mwstEntries = [],
  phaseNames = {},
  startSaldoMitPrognose = null
) => {
  const weeks = [];
  const today = new Date();
  const eigenanteil = buildEigenanteilKontext(budgetEntries, phaseNames);

  const allEntries = [...budgetEntries, ...mwstEntries];

  for (let i = 0; i < weekCount; i++) {
    const weekStart = getWeekStart(startDate, i);
    const weekEnd = getWeekEnd(weekStart);

    weeks.push({
      weekNumber: getWeekNumber(weekStart),
      weekStart: weekStart,
      weekEnd: weekEnd,
      label: formatWeekLabel(weekStart),
      shortLabel: formatShortWeekLabel(weekStart),
      einnahmen: 0,
      ausgaben: 0,
      einnahmenPrognose: 0,
      ausgabenPrognose: 0,
      einnahmenOverdue: 0,
      // UI-Split von einnahmen (Summe bleibt massgeblich für alle Salden):
      // voll grün nur mit versendeter Rechnung, sonst transparenter
      einnahmenVersendet: 0,
      einnahmenUnversendet: 0,
      ausgabenProjekt: 0,
      netto: 0,
      nettoMitPrognose: 0,
      kumulativ: 0,
      kumulativMitPrognose: 0,
      kumulativEigenanteil: 0,
      subplanerSchuld: 0,
      budgetDetails: [],
      mwstDetails: [],
      weekIndex: i,
    });
  }

  allEntries.forEach((entry) => {
    const entryDate = new Date(entry.versand_datum || entry.wann || entry.ab_wann);

    // Skip entries with invalid dates
    if (isNaN(entryDate.getTime())) return;

    // Skip entries that are already in the journal (ist_erfasst)
    if (entryDate <= today && entry.ist_erfasst) {
      return;
    }

    // Skip paid entries - they're already in the bank balance
    if (entry.status === "paid" || entry.paid_date) {
      return;
    }

    if (entry.typ_timing === "Einmalig" || entry.ist_mwst_abrechnung) {
      distributeEinmaligeEintrag(entry, weeks, entry.ist_prognose, eigenanteil.quoteFuer, phaseNames);
    } else {
      distributeRegelmässigeEintrag(entry, weeks, entry.ist_prognose, allEntries, eigenanteil.quoteFuer, phaseNames);
    }
  });

  let laufenderSaldo = startSaldo;
  let laufenderSaldoMitPrognose =
    startSaldoMitPrognose != null ? startSaldoMitPrognose : startSaldo;
  weeks.forEach((week) => {
    // Include overdue income in the cashflow calculation
    week.netto = week.einnahmen + week.einnahmenOverdue - week.ausgaben;
    const gesamtEinnahmen = week.einnahmen + week.einnahmenOverdue + week.einnahmenPrognose;
    const gesamtAusgaben = week.ausgaben + week.ausgabenPrognose;
    week.nettoMitPrognose = gesamtEinnahmen - gesamtAusgaben;

    laufenderSaldo += week.netto;
    laufenderSaldoMitPrognose += week.nettoMitPrognose;

    week.kumulativ = laufenderSaldo;
    week.kumulativMitPrognose = laufenderSaldoMitPrognose;

    // Eigenanteil ist eine BESTANDSgrösse: vom Saldo abgezogen wird die zu diesem
    // Zeitpunkt offene, anteilig verdiente Schuld gegenüber Subplaner:innen.
    // Absolut gerechnet (nicht kumuliert) — damit stimmt sie auch für nachgeladene
    // Wochen, die nicht bei Woche 1 beginnen.
    week.subplanerSchuld = eigenanteil.schuldAm(week.weekEnd);
    week.kumulativEigenanteil = week.kumulativ - week.subplanerSchuld;
  });

  return weeks;
};

// ===== Schwellen-Analyse =====

// Erste Woche des letzten zusammenhängenden Laufs unter der Schwelle, der bis
// zum Horizont-Ende reicht (null = die letzte Woche liegt über der Schwelle,
// d.h. der Saldo erholt sich innerhalb des Horizonts wieder)
const findDauerhaftUnter = (weeks, schwelle) => {
  if (!weeks.length || weeks[weeks.length - 1].kumulativ >= schwelle) return null;
  let i = weeks.length - 1;
  while (i > 0 && weeks[i - 1].kumulativ < schwelle) i--;
  return weeks[i];
};

// Wochen, in denen der kumulierte Saldo die Warnschwelle unterschreitet bzw.
// negativ wird (null = im betrachteten Horizont nicht):
// - unterschreitung/negativ: ERSTE Woche unter der Schwelle (kann ein kurzer
//   Einbruch sein, z.B. durch nachgerückte offene Alt-Posten)
// - unterschreitungDauerhaft/negativDauerhaft: ab dieser Woche bleibt der
//   Saldo bis zum Horizont-Ende darunter (null = erholt sich wieder)
export const findThresholdDates = (weeks, warnschwelle) => {
  const unterschreitung = weeks.find((w) => w.kumulativ < warnschwelle) || null;
  const negativ = weeks.find((w) => w.kumulativ < 0) || null;
  return {
    unterschreitung,
    negativ,
    unterschreitungDauerhaft: findDauerhaftUnter(weeks, warnschwelle),
    negativDauerhaft: findDauerhaftUnter(weeks, 0),
  };
};
